import { FC } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircleIcon, HomeIcon, SparklesIcon } from "@heroicons/react/24/outline";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Tag } from "lucide-react";

export interface SubscriptionConfirmationPageProps {
  className?: string;
}

const SubscriptionActivePage: FC<SubscriptionConfirmationPageProps> = ({ className = "" }) => {
  const navigate = useNavigate();
  const { name } = useParams();
  const handleExploreLocations = () => {
    navigate("/espace-evenementiel");
  };

  const handleReturnHome = () => {
    navigate("/client/abonnement");
  };

  const renderMain = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col w-full p-6 space-y-8 bg-white border shadow-xl rounded-2xl border-neutral-200 dark:border-neutral-700 dark:bg-neutral-800 xl:p-8"
    >
      {/* Header */}
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.3 }}
        className="text-center"
      >
        <CheckCircleIcon className="w-16 h-16 mx-auto text-green-500" />
        <h2 className="mt-4 text-2xl font-bold text-neutral-900 dark:text-neutral-100 sm:text-3xl">
          Vous avez déjà un abonnement actif
        </h2>
        <p className="text-neutral-600 dark:text-neutral-400">
          Votre abonnement <span className="font-bold text-black">{name}</span> est actuellement actif.
        </p>
      </motion.div>

      <hr className="border-neutral-200 dark:border-neutral-700" />

      {/* Confirmation Message */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4 text-center"
      >
        <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
          Votre abonnement est en cours !
        </h3>
        <p className="text-neutral-600 dark:text-neutral-400">
          Votre espace bénéficie d’une visibilité optimale sur EventToBee.
        </p>
        <p className="text-neutral-600 dark:text-neutral-400">
          Continuez à gérer vos demandes depuis votre tableau de bord et restez attentif aux nouvelles
          opportunités.
        </p>
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex flex-col gap-4 sm:flex-row sm:justify-center"
      >
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <ButtonPrimary
            onClick={handleExploreLocations}
            className="flex items-center justify-center w-full gap-2 bg-indigo-600 hover:bg-indigo-700 sm:w-auto"
          >
            <SparklesIcon className="w-5 h-5" />
            Explorer les espaces
          </ButtonPrimary>
        </motion.div>
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <ButtonPrimary
            onClick={handleReturnHome}
            className="flex items-center justify-center w-full gap-2 bg-gray-600 hover:bg-gray-700 sm:w-auto"
          >
            <Tag className="w-5 h-5" />
            Voir le détail de l'abonnement{" "}
          </ButtonPrimary>
        </motion.div>
      </motion.div>
    </motion.div>
  );

  return (
    <div className={`nc-SubscriptionConfirmationPage ${className}`}>
      <main className="container mb-24 mt-11 lg:mb-32">
        <AnimatePresence>
          <motion.div
            key="main"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="max-w-4xl mx-auto"
          >
            {renderMain()}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default SubscriptionActivePage;
